/**
 * Lo que dice la mascota en su globo, bajo el saludo.
 *
 * ── Por qué hay dos clases de frase ─────────────────────────────────────────
 * Cien frases escritas de antemano son cien frases que no saben nada de ti. La
 * primera vez tienen gracia; a la décima, el sitio en el que aparecen es sitio
 * que se aprende a no mirar — y ése es justo el renglón donde queremos poder
 * decirle algo que importe.
 *
 * Así que hay dos: los HECHOS, que los calcula quien tiene los datos delante
 * («tus visitas subieron un 24%», «tu sitio no está publicado»), y las FRASES,
 * escritas aquí. Cuando hay un hecho que contar, gana el hecho. Las frases
 * rellenan el resto de los días, que son la mayoría.
 *
 * Un hecho puede traer su ACCIÓN, y casi todos deberían: decir «tu sitio no
 * está publicado» sin un botón al lado es dejar el problema señalado y al
 * cliente buscando dónde se arregla. Las frases escritas no llevan botón —no
 * señalan nada— y por eso el botón vive en el hecho y no en el renglón.
 *
 * Y no se repite la de la última vez: dos cargas seguidas con el mismo texto
 * hacen parecer que el renglón está roto, no que la mascota se calló.
 *
 * ── Lo que NO puede decir ───────────────────────────────────────────────────
 * Nada que deje la duda de que algo va mal. Ni olvidos («se me ha olvidado»),
 * ni averías hipotéticas («si algo se rompe»), ni revisiones que insinúan que
 * había algo que revisar («he mirado los servidores»). Son graciosas leídas de
 * una en una y son un susurro de que el producto falla leídas en el panel donde
 * alguien tiene su negocio montado. La mascota puede ser simpática; no puede
 * sembrar dudas sobre lo que la rodea.
 *
 * Tampoco nada que señale dónde está una cosa —«el buscador de aquí abajo»—:
 * las cosas se mueven, y una frase que apunta a un sitio se queda mintiendo el
 * día que se mueven. Ver el buscador, que empezó en este globo y hoy vive en la
 * barra de arriba.
 */

import { translate } from './i18n'

/**
 * Algo que la mascota puede decir, con o sin el camino para atenderlo.
 */
export type MascotFact = {
  text: string
  /** El botón que sale al lado. Sólo cuando hay algo concreto que hacer. */
  action?: { label: string; run: () => void }
}

/**
 * Las frases, por tono.
 *
 * Se guardan en tres montones y no en una lista sola para poder sortear entre
 * ellos: sin eso, el montón más grande —los consejos, que son los que más se
 * escriben— saldría casi siempre y la mascota parecería un manual.
 *
 * Lo que hay aquí son CLAVES del catálogo, no las frases. Esto son constantes de
 * módulo y no pueden llamar a un hook, así que con el texto escrito aquí la
 * mascota seguiría hablando en español en un panel en inglés — y siendo lo que
 * más carácter le da al producto, es de lo último que convendría dejar a medias.
 * Las versiones inglesas no son traducciones literales: un chiste traducido
 * palabra por palabra deja de ser un chiste.
 */

/** Ella hablando de sí misma o del momento. Es lo que la hace un personaje. */
const PLAYFUL = [
  'mascot.playful.1',
  'mascot.playful.2',
  'mascot.playful.3',
  'mascot.playful.4',
  'mascot.playful.5',
  'mascot.playful.6',
  'mascot.playful.7',
  'mascot.playful.8',
  'mascot.playful.9',
  'mascot.playful.10',
  'mascot.playful.11',
  'mascot.playful.12',
  'mascot.playful.13',
  'mascot.playful.14',
  'mascot.playful.15',
  'mascot.playful.16',
  'mascot.playful.17',
  'mascot.playful.18',
  'mascot.playful.19',
  'mascot.playful.20',
  'mascot.playful.21',
  'mascot.playful.22',
  'mascot.playful.23',
  'mascot.playful.24',
  'mascot.playful.25',
  'mascot.playful.26',
  'mascot.playful.27',
  'mascot.playful.28',
  'mascot.playful.29',
  'mascot.playful.30',
]

/** Lo que de verdad le conviene saber a quien tiene un sitio. */
const TIPS = [
  'mascot.tip.1',
  'mascot.tip.2',
  'mascot.tip.3',
  'mascot.tip.4',
  'mascot.tip.5',
  'mascot.tip.6',
  'mascot.tip.7',
  'mascot.tip.8',
  'mascot.tip.9',
  'mascot.tip.10',
  'mascot.tip.11',
  'mascot.tip.12',
  'mascot.tip.13',
  'mascot.tip.14',
  'mascot.tip.15',
  'mascot.tip.16',
  'mascot.tip.17',
  'mascot.tip.18',
  'mascot.tip.19',
  'mascot.tip.20',
  'mascot.tip.21',
  'mascot.tip.22',
  'mascot.tip.23',
  'mascot.tip.24',
  'mascot.tip.25',
  'mascot.tip.26',
  'mascot.tip.27',
  'mascot.tip.28',
  'mascot.tip.29',
  'mascot.tip.30',
  'mascot.tip.31',
  'mascot.tip.32',
  'mascot.tip.33',
  'mascot.tip.34',
  'mascot.tip.35',
  'mascot.tip.36',
  'mascot.tip.37',
  'mascot.tip.38',
  'mascot.tip.39',
  'mascot.tip.40',
]

/** El producto explicándose sin sonar a manual. */
const PRODUCT = [
  'mascot.product.1',
  'mascot.product.2',
  'mascot.product.3',
  'mascot.product.4',
  'mascot.product.5',
  'mascot.product.6',
  'mascot.product.7',
  'mascot.product.8',
  'mascot.product.9',
  'mascot.product.10',
  'mascot.product.11',
  'mascot.product.12',
  'mascot.product.13',
  'mascot.product.14',
  'mascot.product.15',
  'mascot.product.16',
  'mascot.product.17',
  'mascot.product.18',
  'mascot.product.19',
  'mascot.product.20',
  'mascot.product.21',
  'mascot.product.22',
  'mascot.product.23',
]

const POOLS = [PLAYFUL, TIPS, PRODUCT]

/** Dónde se recuerda la última, para no repetirla seguida. */
const LAST_KEY = 'veltica_mascot_line'

function readLast(): string {
  try {
    return localStorage.getItem(LAST_KEY) ?? ''
  } catch {
    return ''
  }
}

function rememberLast(line: string): void {
  try {
    localStorage.setItem(LAST_KEY, line)
  } catch {
    /* Sin almacenamiento se podrá repetir alguna vez. No es grave. */
  }
}

function pick<T>(list: readonly T[]): T | undefined {
  return list[Math.floor(Math.random() * list.length)]
}

/**
 * La frase de esta visita.
 *
 * `facts` son las frases que sí saben algo —las arma quien tiene los datos— y
 * pesan el doble que las escritas: cuando hay algo real que contar, contarlo es
 * mejor que hacer una gracia. No siempre, para que la mascota no se vuelva un
 * panel de cifras y siga teniendo carácter.
 */
export function pickMascotLine(facts: readonly MascotFact[] = []): MascotFact {
  const last = readLast()
  const useFact = facts.length > 0 && Math.random() < 0.66
  const options: MascotFact[] = useFact
    ? [...facts]
    : (pick(POOLS) ?? PLAYFUL).map((key) => ({ text: translate(key) }))

  /* Se descarta la última dicha, salvo que fuera la única posible. */
  const fresh = options.filter((option) => option.text !== last)
  const chosen = pick(fresh.length > 0 ? fresh : options) ?? { text: translate(PLAYFUL[0] ?? '') }
  rememberLast(chosen.text)
  return chosen
}
